import axios, { AxiosResponse } from 'axios';
import { 
  AFMessage,  
 
} from '../stores/data/types';
import { localStorageManager, STORAGE_KEYS } from '../utils/localStorage';

// API 기본 설정
const API_BASE_URL = 'https://prod-sol-af-tracker.alpha-filter.xyz';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 1000000, // 1000초로 연장
  headers: {
    'Content-Type': 'application/json',
  },
});

// 응답 인터셉터 - 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    throw error;
  }
);

// API 응답 타입 정의
export interface GetTokensResponse {
  [tokenAddress: string]: AFMessage[];
}

export interface GetSnapshotResponse {
  [tokenAddress: string]: AFMessage[];
}

export interface GetStreamsByAddressesRequest {
  addresses: string[];
}

export interface RankTimelineResponse {
  // 실제 API 응답 구조에 맞게 정의 필요
  // 임시로 any로 설정
  [key: string]: any;
}

// Alpha Finder Conviction Metrics API 관련 인터페이스
export interface BulkCreateAlphaFinderTokenConvictionRequest {
  daysFromNow: number;
}

export interface BulkCreateAlphaFinderTokenConvictionByTokenAddressesRequest {
  tokenAddresses: string[];
}

export interface BulkCreateResponse {
  message: string;
}

// AF Management API 관련 인터페이스
export interface EvaluateMultipleAddressesRequest {
  addresses: string[];
}

export interface EvaluateMultipleAddressesResponse {
  results: Record<string, any | null>;
}

// Token Alpha Finder Metrics API 관련 인터페이스
export interface TokenAlphaFinderMetricsResponse {
  tokenAddress: string;
  afCount: number;
  data: Record<string, {
    avgBuyPrice: number;
    netBettingRank: {
      rank: number;
      totalCnt: number;
      value: number;
    } | null;
  }>;
}

export interface TokenAlphaFinderMetricDetailResponse {
  tokenAddress: string;
  afAddress: string;
  ranks: {
    singleBetRanks: any;
    cumulativeBuyRanks: any;
    netBettingRank: number;
  };
}

// Token Conviction Rank Slack Alert 관련 인터페이스
export interface TokenConvictionRankSlackAlertHistory {
  mc: number;
  af_address: string;
  cumulative_conviction_rank: number;
  peak_conviction_rank: number;
  time_to_reach_target_conviction_rank: number;
  created_at: string;
}

export interface TokenConvictionRankSlackAlert {
  _id: string;
  token_address: string;
  token_symbol: string;
  pool_created_at: string;
  latest_mc: number;
  latest_cumulative_conviction_rank: number;
  latest_peak_conviction_rank: number;
  latest_time_to_reach_target_conviction_rank: number;
  first_created_at: string;
  last_updated_at: string;
  total_alert_count: number;
  histories: TokenConvictionRankSlackAlertHistory[];
}

export interface GetSlackAlertsQueryParams {
  page?: number;
  limit?: number;
  sortBy?: 'latest_mc' | 'latest_cumulative_conviction_rank' | 'latest_peak_conviction_rank' | 'latest_time_to_reach_target_conviction_rank' | 'first_created_at' | 'last_updated_at' | 'total_alert_count';
  sortOrder?: 'asc' | 'desc';
  from?: string; // ISO 8601 format date string
  to?: string; // ISO 8601 format date string
}

export interface GetSlackAlertsResponse {
  data: TokenConvictionRankSlackAlert[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// AF 그룹 관련 인터페이스
export interface AFGroup {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AF {
  id: string;
  name: string;
  description: string;
  label?: "legendary";
  point: number;
  address: string;
  groupNames: string;
  syncStatus: string;
  createdAt: string;
  updatedAt: string;
}

// API 함수들
export const api = {

  /**
   * 모든 토큰의 스냅샷 정보를 가져옵니다.
   * @returns 모든 토큰의 모든 타입 정보
   */
  async getSnapshot(): Promise<GetSnapshotResponse> {
    try {
      const response: AxiosResponse<GetSnapshotResponse> = (await apiClient.get(`/aggregated-tokens/snapshot`)).data;
      return response.data;
    } catch (error) {
      console.error('❌ GET /snapshot 에러:', error);
      throw new Error(`스냅샷 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰 주소들의 스트림 정보를 가져옵니다.
   * @param addresses 토큰 주소 배열
   * @returns 각 토큰 주소별 모든 타입의 정보 (snapshot과 동일한 형식)
   */
  async getStreamsByAddresses(addresses: string[]): Promise<GetSnapshotResponse> {
    try {
      if (addresses.length === 0) {
        return {};
      }

      const requestBody: GetStreamsByAddressesRequest = { addresses };
      const response: AxiosResponse<GetSnapshotResponse> = (await apiClient.post(
        `/aggregated-tokens/streams/by-addresses`,
        requestBody
      )).data;
      
      return response.data;
    } catch (error) {
      console.error('❌ POST /aggregated-tokens/streams/by-addresses 에러:', error);
      throw new Error(`스트림 정보 조회 실패: ${error}`);
    }
  },

  /**
   * 토큰 주소 배열을 콤마로 구분된 문자열로 변환합니다.
   * @param addresses 토큰 주소 배열
   * @returns 콤마로 구분된 문자열
   */
  formatAddresses(addresses: string[]): string {
    return addresses.join(',');
  },

  /**
   * 특정 토큰의 랭크 타임라인 데이터를 가져옵니다.
   * @param tokenAddress 토큰 주소
   * @param targetAmountInUsd 목표 금액 (기본값: 5000)
   * @param signal AbortSignal (기본값: undefined)
   * @returns 랭크 타임라인 데이터
   */
  async getRankTimeline(tokenAddress: string, targetAmountInUsd: number = 5000, signal?: AbortSignal): Promise<RankTimelineResponse> {
    try {
      const response: AxiosResponse<RankTimelineResponse> = await apiClient.get(
        `/alpha-finder-conviction-metrics/${tokenAddress}/rank-timeline`,
        {
          params: { targetAmountInUsd },
          signal, // AbortSignal 추가
          headers: {
            'X-API-Key': localStorageManager.getStringItem(STORAGE_KEYS.API_KEY) || '',
            accept: '*/*',
            'Content-Type': 'application/json'
          },  
        }
      );
      
      return response.data.data;
    } catch (error) {
      console.error('❌ GET /alpha-finder-conviction-metrics/rank-timeline 에러:', error);
      throw new Error(`랭크 타임라인 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 AF 그룹의 AF 목록을 가져옵니다.
   * @param afGroupId AF 그룹 ID
   * @returns AF 목록
   */
  async getAFsByGroupId(afGroupId: string = "67487a844de00d9916e29117"): Promise<AF[]> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);

      const response: AxiosResponse<AF[]> = await apiClient.get(
        `/af-groups/${afGroupId}/afs`,
        {
          headers: {
            'X-API-Key': apiKey,
            'Content-Type': 'application/json',
          },
        }
      );
      
      return (response.data as any).data as any;
    } catch (error) {
      console.error('❌ GET /af-groups/{afGroupId}/afs 에러:', error);
      throw new Error(`AF 그룹 조회 실패: ${error}`);
    }
  },

  /**
   * 여러 AF 주소의 평가 결과를 조회합니다.
   * @param addresses AF 주소 배열
   * @returns 주소별 평가 결과 (주소가 키, 결과가 값인 객체)
   */
  async evaluateMultipleAddresses(
    addresses: string[]
  ): Promise<EvaluateMultipleAddressesResponse> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);

      const requestBody: EvaluateMultipleAddressesRequest = {
        addresses,
      };

      const response: AxiosResponse<any> = await apiClient.post(
        `/af-management/evaluate-multiple-addresses`,
        requestBody,
        {
          headers: {
            'X-API-Key': apiKey,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ POST /af-management/evaluate-multiple-addresses 에러:', error);
      throw new Error(`다중 AF 주소 평가 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰의 Alpha Finder 메트릭스를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @returns 토큰의 Alpha Finder 메트릭스 정보
   */
  async getTokenAlphaFinderMetrics(
    tokenAddress: string
  ): Promise<TokenAlphaFinderMetricsResponse> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/alpha-finder-conviction-metrics/${tokenAddress}/alpha-finders`
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /alpha-finder-conviction-metrics/{tokenAddress}/alpha-finders 에러:', error);
      throw new Error(`토큰 Alpha Finder 메트릭스 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰과 AF 주소의 상세 Alpha Finder 메트릭스를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @param afAddress AF 주소
   * @returns 특정 AF의 상세 메트릭스 정보
   */
  async getTokenAlphaFinderMetricDetail(
    tokenAddress: string,
    afAddress: string
  ): Promise<any> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/alpha-finder-conviction-metrics/${tokenAddress}/alpha-finders/${afAddress}`
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /alpha-finder-conviction-metrics/{tokenAddress}/alpha-finders/{afAddress} 에러:', error);
      throw new Error(`토큰 Alpha Finder 상세 메트릭스 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰의 Buy/Sell Wall Chart 데이터를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @param endDate 종료 날짜 (ISO string, 기본값: 현재 시간)
   * @returns Buy/Sell Wall Chart 데이터
   */
  async getBuySellWallChart(
    tokenAddress: string,
    endDate?: string
  ): Promise<any> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);
      const endDateParam = endDate || new Date().toISOString();

      const response: AxiosResponse<any> = await apiClient.get(
        `/token-investigations/${tokenAddress}/buy-sell-wall-chart`,
        {
          params: { endDate: endDateParam },
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /token-investigations/{tokenAddress}/buy-sell-wall-chart 에러:', error);
      throw new Error(`Buy/Sell Wall Chart 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰에 투자한 계정 리스트 및 데이터를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @param params 쿼리 파라미터 (기본값 포함)
   * @param signal AbortSignal (기본값: undefined)
   * @returns 계정 활동 데이터 배열
   */
  async getTokenInvestigations(
    tokenAddress: string,
    params?: {
      endDate?: string;
      timeWindowMinutes?: number;
      lowTxThreshold?: number;
      lowUsedValueThreshold?: number;
      minHoldingPct?: number;
      bigBetSol?: number;
      mmMinTxCount?: number;
      mmMaxAvgIntervalSec?: number;
      cumulativeTradeSol?: number;
      minCummulativeSellCnt?: number;
      mmMinPairs?: number;
      mmMaxAvgHoldingSec?: number;
      allOutThreshold?: number;
    },
    signal?: AbortSignal
  ): Promise<any[]> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);
      
      // 기본값 설정
      const defaultParams = {
        endDate: new Date().toISOString(), // 최신 timestamp
        timeWindowMinutes: 5,
        lowTxThreshold: 100,
        // lowUsedValueThreshold: 4,
        // minHoldingPct: 0.01,
        bigBetSol: 5,
        // mmMinTxCount: 10,
        // mmMaxAvgIntervalSec: 60,
        // cumulativeTradeSol: 5,
        minCummulativeSellCnt: 5,
        // mmMinPairs: 5,
        // mmMaxAvgHoldingSec: 180,
        // allOutThreshold: 0.1,
      };

      const queryParams = { ...defaultParams, ...params };

      const response: AxiosResponse<any> = await apiClient.get(
        `/token-investigations/${tokenAddress}`,
        {
          params: queryParams,
          signal,
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data || [];
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw error;
      }
      console.error('❌ GET /token-investigations/{tokenAddress} 에러:', error);
      throw new Error(`토큰 조사 데이터 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰의 조사 싱크를 시작합니다.
   * @param tokenAddress 토큰 주소
   * @returns 성공 메시지
   */
  async startTokenInvestigationSync(tokenAddress: string): Promise<{ message: string }> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);

      const response: AxiosResponse<{ message: string }> = await apiClient.post(
        `/token-investigations/${tokenAddress}/start-sync`,
        {},
        {
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('❌ POST /token-investigations/{tokenAddress}/start-sync 에러:', error);
      throw new Error(`토큰 조사 싱크 시작 실패: ${error}`);
    }
  },

  /**
   * Slack alerts 리스트를 페이지네이션으로 조회합니다.
   * @param params 쿼리 파라미터 (page, limit, sortBy, sortOrder)
   * @returns Slack alerts 리스트 및 페이지네이션 정보
   */
  async getSlackAlerts(
    params?: GetSlackAlertsQueryParams,
    signal?: AbortSignal
  ): Promise<GetSlackAlertsResponse> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);
      
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 100,
        sortBy: params?.sortBy || 'last_updated_at',
        sortOrder: params?.sortOrder || 'desc',
      };

      // 날짜 필터 파라미터 추가 (있는 경우만, undefined는 제외)
      if (params?.from !== undefined && params.from !== null && params.from !== '') {
        queryParams.from = params.from;
      }
      if (params?.to !== undefined && params.to !== null && params.to !== '') {
        queryParams.to = params.to;
      }

      const response: AxiosResponse<{ data: GetSlackAlertsResponse }> = await apiClient.get(
        `/alpha-finder-conviction-metrics/slack-alerts`,
        {
          params: queryParams,
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
          signal, // AbortSignal 전달
        }
      );

      return response.data.data;
    } catch (error: any) {
      if (error.message === 'canceled') {
        return error;
      }
      console.error('❌ GET /alpha-finder-conviction-metrics/slack-alerts 에러:', error);
      throw new Error(`Slack alerts list lookup failed: ${error}`);
    }
  },

  /**
   * 특정 토큰 주소의 Slack alert 정보를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @returns Slack alert 정보
   */
  async getSlackAlertByTokenAddress(
    tokenAddress: string
  ): Promise<TokenConvictionRankSlackAlert | null> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);

      const response: AxiosResponse<any> = await apiClient.get(
        `/alpha-finder-conviction-metrics/${tokenAddress}/slack-alert`,
        {
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('❌ GET /alpha-finder-conviction-metrics/{tokenAddress}/slack-alert 에러:', error);
      throw new Error(`Slack alert 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 토큰 주소의 Alpha Finder들과 평가 결과를 조회합니다.
   * @param tokenAddress 토큰 주소
   * @returns Alpha Finder 상세 정보 및 평가 결과
   */
  async getTokenAfsWithEvaluation(
    tokenAddress: string
  ): Promise<{
    afDetails: Array<{
      address: string;
      currentTokenHoldingAmount: number;
      identity: string;
      pattern: string[];
      performance: string;
      twitterFollowers: number;
      twitterName: string;
      twitterProfilePicture: string;
      sync: string;
      evaluateResult: {
        afAddress: string;
        tradePattern: string;
        performance: string;
        evaluationPeriod: {
          from: string;
          to: string;
        };
        metrics: {
          pnl: number;
          txCount: number;
          winRate: number;
          riskRewardRatio: number;
          signatureInterval: number;
          avgBuyValuesInUsd: number;
          midBuyValuesInUsd: number;
          avgHoldingTimeInMin: number;
          avgHoldingtimeAtPctDrawdown: number;
          totalBuyValueInUsd: number;
          totalSellValueInUsd: number;
          totalEarnedValueInUsd: number;
          roi: string;
          winCnt: number;
          loseCnt: number;
          mcRangeMetrics: string[];
          bettingRangeMetrics: Array<{
            range: string;
            count: number;
          }>;
          totalInvestCnt: number;
          mmCnt: number;
          scalpingCnt: number;
        };
        highestTradingStatistics: Array<{
          tokenAddress: string;
          pnl: number;
          roi: number;
          inflowCnt: number;
          outflowCnt: number;
          totalInflowInUsd: number;
          totalOutflowInUsd: number;
          tradingBehaviorType: string;
          avgEntryMarketCapInUsd: number;
          maxHoldingDurationSeconds: number;
        }>;
        createdAt: string;
      };
    }>;
    tokenTradeFlows: any[];
  }> {
    try {
      const apiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);

      const response: AxiosResponse<any> = await apiClient.get(
        `/tokens/${tokenAddress}/afs-with-evaluation`,
        {
          headers: {
            'X-API-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('response.data.data', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ GET /tokens/{tokenAddress}/afs-with-evaluation 에러:', error);
      throw new Error(`토큰 Alpha Finder 평가 결과 조회 실패: ${error}`);
    }
  },
};

export default api;
